import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import FormInputField from '../FormInputField';
import Button, { buttonType } from '../../components/Button';
import { useLabels } from '../../hooks';
import { UserContext } from '../../contexts';
import { userApi } from '../../api';
import { formatString } from '../../utils/helpers';
import styles from './LoginForm.module.scss';
import { RefreshIcon, SmsIcon, PhoneIcon } from './CodeOptionIcons';
import { trackEvent } from '../../utils/analytics';

const VerifyCode = ({
    confirmPasswordText,
    confirmPasswordUsername,
    setViewState = () => {},
    setVerifiedCode = () => {},
    setSmsSentToParent = () => {},
    setVoiceCallSentToParent = () => {},
}) => {
    const [error, setError] = useState();
    const [smsMaskedPhone, setSmsMaskedPhone] = useState(null);
    const [voiceMaskedPhone, setVoiceMaskedPhone] = useState(null);
    const [smsSent, setSmsSent] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [smsError, setSmsError] = useState(null);
    const [voiceCallSent, setVoiceCallSent] = useState(false);
    const [voiceCallSending, setVoiceCallSending] = useState(false);
    const [voiceCallError, setVoiceCallError] = useState(null);
    const [resendSent, setResendSent] = useState(false);
    const [resending, setResending] = useState(false);
    const { getLabel, getCognitoApiError } = useLabels();
    const { sendResetPasswordCode } = useContext(UserContext);
    const codeRef = useRef();
    const isActive = useRef(false);

    useEffect(() => {
        isActive.current = true;
        return () => { isActive.current = false; };
    }, []);

    useEffect(() => {
        setSmsMaskedPhone(null);
        setVoiceMaskedPhone(null);
        setSmsSent(false);
        setSmsError(null);
        setVoiceCallSent(false);
        setVoiceCallSending(false);
        setVoiceCallError(null);
        if (!confirmPasswordUsername) return;
        userApi.GetSmsOption(confirmPasswordUsername)
            .then(res => { if (isActive.current && res?.hasPhone) setSmsMaskedPhone(res.maskedPhone); })
            .catch(() => {});
        userApi.GetVoiceOption(confirmPasswordUsername)
            .then(res => { if (isActive.current && res?.hasPhone) setVoiceMaskedPhone(res.maskedPhone); })
            .catch(() => {});
    }, [confirmPasswordUsername]);

    const handleSendViaSms = useCallback(async () => {
        setSmsSending(true);
        setSmsError(null);
        try {
            const res = await userApi.SendSmsOtp(confirmPasswordUsername);
            if (!isActive.current) return;
            if (res?.success) {
                trackEvent('otp_requested', { method: 'sms' });
                setSmsSent(true);
                setSmsSentToParent(true);
            } else {
                setSmsError(getLabel('sms_no_phone'));
            }
        } catch {
            if (isActive.current) setSmsError(getLabel('sms_no_phone'));
        } finally {
            if (isActive.current) setSmsSending(false);
        }
    }, [confirmPasswordUsername, getLabel, setSmsSentToParent]);

    const handleSendViaVoice = useCallback(async () => {
        setVoiceCallSending(true);
        setVoiceCallError(null);
        try {
            const res = await userApi.SendVoiceOtp(confirmPasswordUsername);
            if (!isActive.current) return;
            if (res?.success) {
                trackEvent('otp_requested', { method: 'voice' });
                setVoiceCallSent(true);
                setVoiceCallSentToParent(true);
            } else {
                setVoiceCallError(getLabel('voice_no_phone'));
            }
        } catch {
            if (isActive.current) setVoiceCallError(getLabel('voice_no_phone'));
        } finally {
            if (isActive.current) setVoiceCallSending(false);
        }
    }, [confirmPasswordUsername, getLabel, setVoiceCallSentToParent]);

    const handleResend = useCallback(async () => {
        setSmsSent(false);
        setSmsSentToParent(false);
        setSmsError(null);
        setVoiceCallSent(false);
        setVoiceCallSentToParent(false);
        setVoiceCallError(null);
        setResending(true);
        try {
            await sendResetPasswordCode(confirmPasswordUsername);
            if (isActive.current) setResendSent(true);
        } catch (e) {
            if (isActive.current) setError(e);
        } finally {
            if (isActive.current) setResending(false);
        }
    }, [confirmPasswordUsername, sendResetPasswordCode, setSmsSentToParent, setVoiceCallSentToParent]);

    const handleContinue = useCallback((event) => {
        event.preventDefault();
        if (!codeRef.current.validation()) {
            codeRef.current.focus();
            return;
        }
        setVerifiedCode(codeRef.current.getValue());
        setViewState('set_new_password');
    }, [setVerifiedCode, setViewState]);

    return (
        <>
            <div className={styles.login}>
                <div className={styles.content}>
                    <div>
                        <div className={styles.stepProgress}>
                            <div className={styles.stepLabel}>{getLabel('verify_code_step')}</div>
                            <div className={styles.progressBar}>
                                <div className={styles.progressFill} style={{ width: '50%' }} />
                            </div>
                        </div>
                        <div className={styles.stepTitle}>{getLabel('verify_code_title')}</div>
                        <div className={styles.text} dangerouslySetInnerHTML={{ __html: confirmPasswordText }} />
                        <div className={styles.text} dangerouslySetInnerHTML={{ __html: getLabel('migration_spam_warning') }} />
                        {smsSent && (
                            <div className={styles.text}>{formatString(getLabel('sms_sent_to_phone'), smsMaskedPhone)}</div>
                        )}
                        {voiceCallSent && (
                            <div className={styles.text}>{formatString(getLabel('voice_sent_to_phone'), voiceMaskedPhone)}</div>
                        )}
                        <form className={styles.fields} onSubmit={handleContinue}>
                            <FormInputField
                                label={getLabel('login_form_confirm_code_field')}
                                placeholder={getLabel('login_form_confirm_code_field')}
                                ref={codeRef}
                                required={true}
                                autoComplete="one-time-code"
                                inputMode="numeric"
                                heContentDirection="ltr"
                                hePlaceholderDirection="rtl"
                            />
                            <button hidden={true} />
                        </form>
                    </div>
                    <div className={styles.buttons}>
                        <div>
                            <div className={styles.error} dangerouslySetInnerHTML={{ __html: getCognitoApiError(error) }} />
                            <Button type={buttonType.premium} className={styles.main_button} onClick={handleContinue}>
                                {getLabel('verify_code_continue_button')}
                            </Button>
                        </div>
                    </div>
                    <div className={styles.codeOptions}>
                        <button type="button" className={styles.codeOption} disabled={resendSent || resending} onClick={handleResend}>
                            <RefreshIcon />
                            <span>{resendSent ? getLabel('migration_code_sent_confirmation') : getLabel('migration_resend_code_link')}</span>
                        </button>
                        {smsMaskedPhone && !smsSent && !voiceCallSent && (
                            <button type="button" className={styles.codeOption} disabled={smsSending || voiceCallSending} onClick={handleSendViaSms}>
                                <SmsIcon />
                                <span>{formatString(getLabel('sms_option_label'), smsMaskedPhone)}</span>
                            </button>
                        )}
                        {smsError && <div className={styles.error}>{smsError}</div>}
                        {voiceMaskedPhone && !smsSent && !voiceCallSent && (
                            <button type="button" className={styles.codeOption} disabled={voiceCallSending || smsSending} onClick={handleSendViaVoice}>
                                <PhoneIcon />
                                <span>{formatString(getLabel('voice_option_label'), voiceMaskedPhone)}</span>
                            </button>
                        )}
                        {voiceCallError && <div className={styles.error}>{voiceCallError}</div>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default VerifyCode;
